import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, Plus } from 'lucide-react';
import { clientService, type Client } from '../services/clientService';
import { useAuth } from '../context/AuthContextType';
import PageHeader from '@/components/layout/PageHeader';
import DataTable, { type ColumnDef } from '@/components/shared/DataTable';
import EmptyState from '@/components/shared/EmptyState';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';

interface CreateClientRequest { company_name: string; domain?: string; subscription_tier?: string; max_users?: number; max_projects?: number; }

const tierColor: Record<string, string> = {
  basic:        'bg-slate-50 text-slate-600 border-slate-200',
  professional: 'bg-blue-50 text-blue-700 border-blue-200',
  enterprise:   'bg-purple-50 text-purple-700 border-purple-200',
};

const fmt = (d: string) => new Date(d).toLocaleDateString();

export default function Clients() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const canManage = user && ['SysAdmin', 'DevOps'].includes(user.role ?? '');

  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<CreateClientRequest>({
    company_name: '', domain: '', subscription_tier: 'basic', max_users: 10, max_projects: 10,
  });

  useEffect(() => { loadClients(); }, []);

  const loadClients = async () => {
    try {
      setLoading(true); setError(null);
      setClients(await clientService.getAll());
    } catch (err) {
      setError(err instanceof Error ? err.message : t('pages.clients.errorLoad'));
    } finally { setLoading(false); }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      await clientService.create(form);
      setDialogOpen(false);
      setForm({ company_name: '', domain: '', subscription_tier: 'basic', max_users: 10, max_projects: 10 });
      await loadClients();
    } catch (err) {
      alert(err instanceof Error ? err.message : t('pages.clients.errorCreate'));
    } finally { setSaving(false); }
  };

  const columns: ColumnDef<Client>[] = [
    { key: 'company_name', label: t('pages.clients.companyName'), sortable: true,
      render: c => <span className="font-medium text-slate-800">{c.company_name}</span> },
    { key: 'domain', label: t('pages.clients.domain'), sortable: true,
      render: c => <span className="font-mono text-xs text-slate-500">{c.domain ?? '—'}</span> },
    { key: 'subscription_tier', label: t('pages.clients.subscriptionTier'), sortable: true,
      render: c => c.subscription_tier
        ? <Badge variant="outline" className={`text-[11px] ${tierColor[c.subscription_tier] ?? ''}`}>{t(`pages.clients.tiers.${c.subscription_tier}`)}</Badge>
        : <span className="text-slate-400">—</span> },
    { key: 'max_users', label: t('pages.clients.maxUsers'), sortable: true,
      render: c => <span className="text-slate-600">{c.max_users ?? '—'}</span> },
    { key: 'max_projects', label: t('pages.clients.maxProjects'), sortable: true,
      render: c => <span className="text-slate-600">{c.max_projects ?? '—'}</span> },
    { key: 'created_date', label: t('common.created'), sortable: true,
      render: c => <span className="text-slate-500 text-xs">{fmt(c.created_date)}</span> },
  ];

  if (loading) return (
    <>
      <PageHeader title={t('pages.clients.title')} />
      <div className="px-6 py-4 space-y-2">
        {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-9 w-full" />)}
      </div>
    </>
  );

  if (error) return (
    <>
      <PageHeader title={t('pages.clients.title')} />
      <div className="px-6 py-8 text-sm text-red-600">{error}</div>
    </>
  );

  return (
    <>
      <PageHeader
        title={t('pages.clients.title')}
        subtitle={`${clients.length} client${clients.length !== 1 ? 's' : ''}`}
        actions={canManage ? (
          <Button size="sm" onClick={() => setDialogOpen(true)}>
            <Plus size={14} className="mr-1" />{t('pages.clients.createClient')}
          </Button>
        ) : undefined}
      />

      <div className="px-6 pb-6 pt-4">
        {clients.length === 0 ? (
          <EmptyState
            icon={Building2}
            title={t('pages.clients.noClients')}
            description={t('pages.clients.noClientsDescription')}
            action={canManage ? { label: t('pages.clients.createClient'), onClick: () => setDialogOpen(true) } : undefined}
          />
        ) : (
          <DataTable columns={columns} data={clients} emptyIcon={Building2} emptyTitle={t('pages.clients.noClients')} />
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{t('pages.clients.createModal.title')}</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-3 pt-1">
            <div className="space-y-1">
              <Label>{t('pages.clients.companyName')} *</Label>
              <Input value={form.company_name} onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))} required maxLength={200} />
            </div>
            <div className="space-y-1">
              <Label>{t('pages.clients.domain')}</Label>
              <Input value={form.domain} onChange={e => setForm(f => ({ ...f, domain: e.target.value }))} maxLength={200} placeholder="example.com" />
              <p className="text-xs text-slate-400">{t('pages.clients.domainHelp')}</p>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label>{t('pages.clients.subscriptionTier')} *</Label>
                <Select value={form.subscription_tier} onValueChange={v => setForm(f => ({ ...f, subscription_tier: v }))}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="basic">{t('pages.clients.tiers.basic')}</SelectItem>
                    <SelectItem value="professional">{t('pages.clients.tiers.professional')}</SelectItem>
                    <SelectItem value="enterprise">{t('pages.clients.tiers.enterprise')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>{t('pages.clients.maxUsers')}</Label>
                <Input type="number" min={1} max={10000} value={form.max_users} onChange={e => setForm(f => ({ ...f, max_users: parseInt(e.target.value) || 10 }))} className="h-8 text-xs" />
              </div>
              <div className="space-y-1">
                <Label>{t('pages.clients.maxProjects')}</Label>
                <Input type="number" min={1} max={10000} value={form.max_projects} onChange={e => setForm(f => ({ ...f, max_projects: parseInt(e.target.value) || 10 }))} className="h-8 text-xs" />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
              <Button type="submit" disabled={saving}>{saving ? t('common.saving') : t('common.create')}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

